//Libs
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Typography } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
//App
import { getHistory, useObserver, useRouter } from "@shoothill/core";
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";
//Modles
import { RegistrationTypeEnum } from "Globals/Models/RegistrationTypeEnum";
import { Stores, StoresContext } from "Globals/Stores";
import { theme } from "Globals/Styles/AppTheme";
//Styles
import { Page, Row } from "Globals/Styles/LayoutStyles";
//ViewModels
import AdminApplicantViewModel from "Globals/ViewModels/Applicant/AdminApplicantViewModel";
//Views
import { BackChevron } from "Globals/Views/IconSVGs/BackChevron";
import React, { useContext, useState } from "react";
import styled from "styled-components";
import { IAccordionControls } from "Views/Application/ApplicationFormView";
import { LogEntry } from "./LogEntry";
import { StatusBox } from "./StatusBox";

export const StyledAccordion = styled(Accordion)`
    box-shadow: none;
    background-color: transparent;
    min-height: 39px;
`;
export const StyledAccordionDetails = styled(AccordionDetails)`
    background-color: ${theme.palette.background.default};
    padding: 0;
`;

export const StyledAccordionSummary = styled(AccordionSummary)`
    background-color: ${theme.palette.common.white};
    margin-bottom: 15px;
    min-height: 39px;
    p {
        color: #191919;
        font-weight: bold;
        font-size: 16px;
    }
    .MuiAccordionSummary-content.Mui-expanded {
        margin: 0px;
    }
    .Mui-expanded {
        min-height: 0px;
    }
`;

export const StyledLinkButton = styled(Button)`
    display: flex;
    flex-direction: row;
    justify-content: center;
    font-size: 12px;
    align-items: center;
    background-color: ${theme.palette.hightownBlue.light};
    color: ${theme.palette.common.white};
    padding: 7px 14px;
    margin-right: 30px;
    :hover {
        background-color: ${theme.palette.hightownBlue.light};
    }
`;

export const StyledLogContainer = styled(Box)`
    .logTypeWrapper {
        display: inline-block;
        min-width: 200px;
    }
    .logType {
        color: #7f7f7f;
        font-size: 14px;
    }
    .log-entry {
        padding: 11px 0px;
        border-bottom: 1px solid #706f6f;
    }
    .log-entry:last-of-type {
        border-bottom: none;
        .logType {
            font-size: 14px;
        }
    }
    .log-entry:first-of-type {
        border-bottom: 1px solid #706f6f;
    }
    .log-entry-accordian {
        border-bottom: none;
    }
`;

function Header(viewModel: AdminApplicantViewModel) {
    return (
        <>
            <Box>
                <span style={{ fontSize: "14px", fontWeight: "bold" }}>Admin /</span>
                <span style={{ fontSize: "14px" }}> {viewModel.getFullName}</span>
            </Box>
            <Box display="flex" justifyContent="space-between" marginTop="20px" alignItems="center">
                <Box display="flex" alignItems="center">
                    <div style={{ cursor: "pointer" }} onClick={() => getHistory().goBack()}>
                        <BackChevron />
                    </div>
                    <Typography style={{ marginLeft: "15px", fontSize: "30px" }}>{viewModel.getFullName}</Typography>
                </Box>
                <Box display="flex">
                    <p style={{ fontSize: "14px", fontWeight: "bold" }}>Tel: {viewModel.model.phoneNumber}</p>
                    <p style={{ marginLeft: "30px", fontSize: "14px", fontWeight: "bold" }}>Email: {viewModel.model.emailAddress}</p>
                </Box>
                <StatusBox id={viewModel.model.applicantStatusId} />
            </Box>
        </>
    );
}

export interface MatchParams {
    applicantId?: string;
}

export const AdminApplicantView: React.FC = () => {
    const storesContext = useContext<Stores>(StoresContext);
    const { match } = useRouter();
    const { applicantId } = match.params as MatchParams;
    const [viewModel] = useState(new AdminApplicantViewModel(applicantId));

    const [expanded, setExpanded] = React.useState<string>("");
    const handleChange = (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : "");
    };

    let sortedLogs = viewModel.model.logs.sort((a, b) => (b.date ? new Date(b.date).getTime() : 0) - (a.date ? new Date(a.date).getTime() : 0));
    let sortedApplicationLogs = sortedLogs.filter((log) => log.applicationFormId != "" && log.applicationFormId != null);
    let firstApplicationIndex = sortedLogs.indexOf(sortedApplicationLogs[0]);
    let firstApplicationLog = sortedLogs[firstApplicationIndex];
    return useObserver(() => (
        <Page>
            {Header(viewModel)}
            <hr style={{ margin: "30px 0px", borderStyle: "dashed" }} />
            <Row>
                <StyledLinkButton onClick={() => viewModel.handleResendRegistration(RegistrationTypeEnum.Email)}>Email application link</StyledLinkButton>
                <StyledLinkButton onClick={() => viewModel.handleResendRegistration(RegistrationTypeEnum.SMS)}>SMS application link</StyledLinkButton>
                <StyledLinkButton disabled={viewModel.model.canStartNewApplicationForm} onClick={() => viewModel.updateCanStartNewApplicationForm(true)}>
                    <span style={{ color: "white" }}>New application</span>
                </StyledLinkButton>
            </Row>
            <StyledLogContainer>
                {sortedLogs.length > 5 ? (
                    <>
                        {firstApplicationLog && <LogEntry firstLog={true} log={firstApplicationLog} viewModel={viewModel} />}
                        <StyledAccordion expanded={expanded === `panel`} square onChange={handleChange(`panel`)}>
                            <AccordionSummary
                                style={{ minHeight: "39px", padding: "0px", borderBottom: "1px solid #706f6f" }}
                                expandIcon={<ExpandMoreIcon style={{ color: theme.palette.hightownBlue.light }} />}
                                aria-controls={`panel-content`}
                            >
                                Other logs
                            </AccordionSummary>
                            <StyledAccordionDetails style={{ display: "block" }}>
                                {sortedLogs.map((log, index) => (
                                    <>{index != firstApplicationIndex && <LogEntry key={log.id} log={log} viewModel={index == firstApplicationIndex ? viewModel : undefined} />}</>
                                ))}
                            </StyledAccordionDetails>
                        </StyledAccordion>
                    </>
                ) : (
                    <>
                        {sortedLogs.map((log, index) => (
                            <LogEntry firstLog={index == 0 ? true : undefined} key={log.id} log={log} viewModel={index == firstApplicationIndex ? viewModel : undefined} />
                        ))}
                    </>
                )}
            </StyledLogContainer>
        </Page>
    ));
};
